import React, { useState, useRef, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  TextField,
  Box,
} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import styled from "@material-ui/core/styles/styled";
import { makeApiMessage } from "../../components/src/common";
import { runEngine } from "../../framework/src/RunEngine";
import MessageEnum, { getName } from "../../framework/src/Messages/MessageEnum";
import { FavoriteItem } from "packages/blocks/dashboard/src/TopSellingItemController.web";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

type CurrentYear = {
  catalogue_id: number;
  catalogue_name: string;
  orders: number;
  pieces: number;
  quantity: number;
  amount: number;
};

type Props = {
  currentYear: CurrentYear[];
  favoriteId: FavoriteItem[];
  handleStatusChangeForSelected: (status: string) => void;
};

interface Block {
  id: number;
  name: string;
}

export const configJSON = require("./config");
const baseURL = require("../../framework/src/config").baseURL;

const FavoriteItemsTable = ({
  currentYear,
  favoriteId,
  handleStatusChangeForSelected,
}: Props) => {
  const secondApiCallId = useRef<string | null>(null);
  const favoriteItemApiCallId = useRef<string | null>(null);
  const statusApiCallId = useRef<string | null>(null);
  const isFavoriteLoaded = useRef(false);
  const [favoriteIds, setFavoriteIds] = useState<FavoriteItem[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Initialize state from prop
  useEffect(() => {
    if (favoriteId && favoriteId.length > 0) {
      setFavoriteIds(favoriteId);
      setSelectedProducts(favoriteId.map((f) => f.id));
    }
  }, [favoriteId]);

  const toggleCustomize = () => {
    setCustomizeOpen((prev) => {
      const next = !prev;
      if (next && !isFavoriteLoaded.current) {
        favoriteItemListApi();
        isFavoriteLoaded.current = true;
      }
      return next;
    });
  };

  const hitFavoriteItemApi = (catalogueId: number) => {
    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const requestMessage = makeApiMessage({
      url: `${baseURL}/${configJSON.topSellingFavoriteItemsEndPoint}`,
      method: "POST",
      body: JSON.stringify({ catalogue_id: catalogueId }),
      headers,
    });
    favoriteItemApiCallId.current = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  const favoriteItemListApi = () => {
    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const requestMessage = makeApiMessage({
      url: `${baseURL}/${configJSON.getFavoriteItemsEndPoint}`,
      method: "GET",
      headers,
    });
    secondApiCallId.current = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  const MAX_SELECTION = 15;
  const favoriteIdNumbers = favoriteIds.map((f) => Number(f.id)); // force number

  const filteredItems =
    favoriteIdNumbers.length > 0
      ? currentYear.filter(
          (item) => favoriteIdNumbers.includes(Number(item.catalogue_id)) // force number
        )
      : [];

  const [tableData, setTableData] = useState<CurrentYear[]>([]);
  type SortKey = "catalogue_name" | "orders" | "pieces" | "amount";

  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: "asc" | "desc";
  } | null>(null);
  const prepareTableData = () => {
    if (!favoriteIds || favoriteIds.length === 0) return [];

    return favoriteIds.map((fav) => {
      const currentItem = currentYear.find(
        (c) => Number(c.catalogue_id) === Number(fav.id)
      );
      return {
        catalogue_id: fav.id,
        catalogue_name: fav.name,
        orders: currentItem ? currentItem.orders : 0,
        pieces: currentItem ? currentItem.pieces : 0,
        quantity: currentItem ? currentItem.quantity : 0,
        amount: currentItem ? currentItem.amount : 0,
      };
    });
  };

  useEffect(() => {
    setTableData(prepareTableData());
  }, [favoriteIds, currentYear]);

  const sortedItems = React.useMemo(() => {
    if (!sortConfig) return tableData;

    return [...tableData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }

      return sortConfig.direction === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [tableData, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const [selectedStatus, setSelectedStatus] = useState("");
  const [productsList, setProductsList] = useState<any[]>([]);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
         `${baseURL}/${configJSON.productGroupListEndPoint}?filter_by[section_ids]=1,2,3,4,5,6&page_no=1&per_page=100`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { token: token } : {}),
            },
          }
        );
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        if (data?.data) {
          setProductsList(data.data);
        }
      } catch (error) {
        console.error("Error fetching products list:", error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div>
      <HeaderRow>
        <h2>Favorite Items</h2>
        <Button variant='contained' color='primary' onClick={toggleCustomize}>
          Customize Product
        </Button>
      </HeaderRow>
      {customizeOpen && (
        <CustomizePanel>
          <div>
            <TextLabel>Select Products *</TextLabel>
            <ClickAwayListener onClickAway={() => setCustomizeOpen(false)}>
              <div>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={productsList}
                  getOptionLabel={(option: any) =>
                    option.attributes.product_name
                  }
                  value={productsList.filter((p) =>
                    selectedProducts.includes(Number(p.id))
                  )}
                  onChange={(event, value) => {
                    const newIds = value.map((v: any) => Number(v.id));
                    if (newIds.length > MAX_SELECTION) return;
                    setSelectedProducts(newIds);

                    const addedIds = newIds.filter(
                      (newId) => !favoriteIds.map((f) => f.id).includes(newId)
                    );
                    const removedIds = favoriteIds
                      .map((f) => f.id)
                      .filter((id) => !newIds.includes(id));
                    const updatedFavoriteIds = favoriteIds
                      .filter((f) => !removedIds.includes(f.id))
                      .concat(
                        addedIds.map((addedId) => {
                          const product = productsList.find(
                            (p) => Number(p.id) === addedId
                          );
                          return {
                            id: addedId,
                            name: product
                              ? product.attributes.product_name
                              : "Unknown Product",
                          };
                        })
                      );

                    setFavoriteIds(updatedFavoriteIds);
                    addedIds.forEach((id) => {
                      hitFavoriteItemApi(id);
                    });
                    removedIds.forEach((id) => {
                      hitFavoriteItemApi(id); 
                    });
                  }}

  
                  renderOption={(option: any, { selected }) => (
                    <OptionRow>
                      <Checkbox
                        checked={selected}
                        disabled={
                          !selected && selectedProducts.length >= MAX_SELECTION
                        }
                      />
                      <span>{option.attributes.product_name}</span>
                    </OptionRow>
                  )}
                  renderTags={(value: any[], getTagProps) => (
                    <ChipContainer>
                      {value.map((option, index) => (
                        <StyledChip
                          key={option.id}
                          label={option.attributes.product_name}
                          {...getTagProps({ index })}
                        />
                      ))}
                    </ChipContainer>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      placeholder='Select Products'
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          padding: "10px",
                          borderRadius: "8px",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    />
                  )}
                />
              </div>
            </ClickAwayListener>
          </div>

          {selectedProducts.length >= 15 && (
            <LimitText>You can select up to 15 products only.</LimitText>
          )}
        </CustomizePanel>
      )}

      <select
        value={selectedStatus}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const value = e.target.value;
          setSelectedStatus(value);
          handleStatusChangeForSelected(value);
        }}
        style={{
          padding: "8px 12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
          backgroundColor: "#f0f8ff",
          color: "#333",
          fontWeight: 500,
          minWidth: "160px",
          cursor: "pointer",
        }}
      >
          <option value=''>Select Status</option>
        <option value='1'>Laundry</option>
        <option value='2'>Dry Cleaning</option>
        <option value='3'>Pressing</option>
        <option value='4'>Cancelled</option>
      </select>

      <StyledTable>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("catalogue_name")}
                style={{ cursor: "pointer" }}
              >
                Product Name{" "}
                {sortConfig?.key === "catalogue_name"
                  ? sortConfig.direction === "asc"
                    ? "▲"
                    : "▼"
                  : "⇅"}
              </TableCell>

              <TableCell
                onClick={() => handleSort("orders")}
                style={{ cursor: "pointer" }}
              >
                Orders{" "}
                {sortConfig?.key === "orders"
                  ? sortConfig.direction === "asc"
                    ? "▲"
                    : "▼"
                  : "⇅"}
              </TableCell>

              <TableCell
                onClick={() => handleSort("pieces")}
                style={{ cursor: "pointer" }}
              >
                Pieces{" "}
                {sortConfig?.key === "pieces"
                  ? sortConfig.direction === "asc"
                    ? "▲"
                    : "▼"
                  : "⇅"}
              </TableCell>

              <TableCell
                onClick={() => handleSort("amount")}
                style={{ cursor: "pointer" }}
              >
                Amount{" "}
                {sortConfig?.key === "amount"
                  ? sortConfig.direction === "asc"
                    ? "▲"
                    : "▼"
                  : "⇅"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((item) => (
              <TableRow key={item.catalogue_id}>
                <TableCell>{item.catalogue_name}</TableCell>
                <TableCell>{item.orders}</TableCell>
                <TableCell>{item.pieces}</TableCell>
                <TableCell>{item.amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTable>
    </div>
  );
};

const HeaderRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
});

const CustomizePanel = styled("div")({
  width: "90%",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  padding: 0,
  background: "none",
  boxShadow: "none",
  borderRadius: 0,
  gap: 12,
});

const OptionRow = styled("li")({
  display: "flex",
  alignItems: "center",
  padding: "3px 8px",
  fontSize: "13px",
  lineHeight: "18px",

  "&:hover": {
    backgroundColor: "#f5f7fa",
  },

  "& span": {
    marginLeft: 4,
    fontWeight: 500,
  },

  "& .MuiCheckbox-root": {
    padding: "2px",
  },
});

const ChipContainer = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 7,
});

const StyledChip = styled(Chip)({
  backgroundColor: "#e3f2fd",
  color: "#1565c0",
  fontWeight: 500,
  borderRadius: 8,
  height: 20,
  fontSize: 12,
  margin: 2,
  "& .MuiChip-label": {
    paddingLeft: 6,
    paddingRight: 5,
  },
  "& .MuiChip-deleteIcon": {
    fontSize: 16,
    marginRight: 2,
    color: "#1565c0",
  },
});

const LimitText = styled("p")({
  color: "#d32f2f",
  fontSize: 13,
  fontWeight: 500,
});

export const TextLabel = styled("label")({
  display: "block",
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 600,
  paddingBottom: 4,
  color: "var(--neutrals-cool-gray-700, #334155)",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "13px",
  },
});

const StyledTable = styled("div")({
  overflowX: "auto",
  "& table": {
    minWidth: 650,
    borderCollapse: "collapse",
  },
  "& th, & td": {
    padding: 8,
    borderBottom: "1px solid #ddd",
    textAlign: "left",
  },
  "& th": {
    fontWeight: 600,
  },
});

export default FavoriteItemsTable;
